// Packages
import moment from 'moment'
// Helpers
import {
  mapV2ResponseAmountToV1ResponseAmount,
} from '../helpers'
import { reserveAdjustmentReasons } from '../../constants-v2'

/**
 * Maps the V1 request body and V1 response body to a V2 request body
 *
 * @typedef {import('../../api-v2/reserve').CreateReserveRequest} CreateReserveRequest
 * @param {*} v1RequestBody - Represents the V1 create request body
 * @param {*} v1ResponseBody - Represents the V1 create response body
 * @param {*} ingestingFromV2 - Whether we're ingesting into V2 then to V1
 * @returns {CreateReserveRequest}
 */
// eslint-disable-next-line max-len
export const mapV1ReserveRequestAndResponseBodyToV2ReserveCreateRequestBody = (v1RequestBody, v1ResponseBody, ingestingFromV2 = false) => {
  return {
    __ingestThrough: ingestingFromV2,
    __v1Payload: v1RequestBody,
    amount: v1RequestBody.amount,
    id: v1ResponseBody.id,
    note: v1RequestBody.notes,
    payload: mapV1RequestBodyToV2RequestBodyMetadata(v1RequestBody),
  }
}

/**
 * @typedef {import('../../api-v2/reserve').V2ReservePayload} V2ReservePayload
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2ReservePayload | {}}
 */
const mapV1RequestBodyToV2RequestBodyMetadata = (v1RequestBody) => {
  switch (v1RequestBody.reason) {
    case 'balance transfer from (positive)':
      return mapV1RequestBodyToV2TransferFromPositiveMetadata(v1RequestBody)
    case 'balance transfer to (positive)':
      return mapV1RequestBodyToV2TransferToPositiveMetadata()
    case 'direct payment by client':
      return mapV1RequestBodyToV2DirectPaymentByClientMetadata(v1RequestBody)
    case 'non-factored payment':
      return mapV1RequestBodyToV2NonFactoredPaymentMetadata(v1RequestBody)
    case 'release to 3rd party':
      return mapV1RequestBodyToV2ReleaseOfFundsTo3rdPartyMetadata(v1RequestBody)
    default:
      return mapV1RequestBodyToV2DefaultMetadata(v1RequestBody)
  }
}

const mapV1RequestBodyReasonToV2RequestBodyMetadataType = (v1RequestBody) => {
  const v1TypesToV2Types = {
    'balance transfer from (positive)': 'transferFromPositive',
    'balance transfer to (positive)': 'transferToPositive',
    'client credit': 'clientCredit',
    'debtor claim': 'brokerClaim',
    'direct payment by client': 'directPaymentByClient',
    'fee': 'fee',
    'non-factored payment': 'nonFactoredPayment',
    'overadvance': 'overadvance',
    'release of funds': 'releaseOfFunds',
    'release to 3rd party': 'releaseOfFundsTo3rdParty',
    'write off': 'writeOff',
  }

  return v1TypesToV2Types[v1RequestBody.reason]
}

/**
 * @typedef {import('../reserve').V2DefaultReserveMetadata} V2DefaultReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2DefaultReserveMetadata}
 */
const mapV1RequestBodyToV2DefaultMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
  }
}

/**
 * @typedef {import('../reserve').V2DirectPaymentByClientReserveMetadata} V2DirectPaymentByClientReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2DirectPaymentByClientReserveMetadata}
 */
const mapV1RequestBodyToV2DirectPaymentByClientMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    receivedDate: moment(parseInt(v1RequestBody.metadata.date_received)).toISOString(),
  }
}

/**
 * @typedef {import('../reserve').V2NonFactoredPaymentReserveMetadata} V2NonFactoredPaymentReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2NonFactoredPaymentReserveMetadata}
 */
const mapV1RequestBodyToV2NonFactoredPaymentMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    batchDate: moment(parseInt(v1RequestBody.metadata.date_received)).toISOString(),
    brokerId: v1RequestBody.metadata.debtor_id,
    brokerName: v1RequestBody.metadata.debtor_name,
    checkNumber: v1RequestBody.metadata.check_number,
    loadNumber: v1RequestBody.metadata.load_number,
    type: v1RequestBody.metadata.transfer_type === "check"
      ? "Check"
      : "ACH",
  }
}

/* eslint-disable max-len */
/**
 * @typedef {import('../reserve').V2ReleaseOfFundsTo3rdPartyReserveMetadata} V2ReleaseOfFundsTo3rdPartyReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2ReleaseOfFundsTo3rdPartyReserveMetadata}
 */
/* eslint-enable max-len */
const mapV1RequestBodyToV2ReleaseOfFundsTo3rdPartyMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    thirdParty: v1RequestBody.metadata.third_party,
  }
}

/**
 * @typedef {import('../reserve').V2TransferFromReserveMetadata} V2TransferFromReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2TransferFromReserveMetadata}
 */
const mapV1RequestBodyToV2TransferFromPositiveMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    transferClientId: v1RequestBody.transfer_clientid,
  }
}

/**
 * @typedef {import('../reserve').V2TransferToReserveMetadata} V2TransferToReserveMetadata
 * @param {*} v1RequestBody - Represents the V1 reserve create request body
 * @returns {V2TransferToReserveMetadata}
 */
const mapV1RequestBodyToV2TransferToPositiveMetadata = (v1RequestBody) => {
  return {
    __payloadType: mapV1RequestBodyReasonToV2RequestBodyMetadataType(v1RequestBody),
    transferClientId: v1RequestBody.transfer_clientid,
  }
}

const mapV2ReserveResponseAmountToV1NachaSign = (v2ResponseAmount) => {
  return Math.sign(v2ResponseAmount) >= 0
    ? '+'
    : '-'
}

const mapV2ReserveResponseToV1Type = (v2Response) => {
  if (v2Response.payload?.__payloadType === 'chargeback') {
    return 'approved invoice'
  }
  else if (v2Response.brokerPaymentId) {
    return 'debtor payment'
  }
  else if (reserveAdjustmentReasons.includes(v2Response.payload?.__payloadType)) {
    return 'client adjustments'
  }
  return ''
}

/**
 * @typedef {object} V1ReserveResponse
 * @param {import('../../api-v2/reserve').V2ReserveResponse} v2ReserveResponse Response from V2
 * @property {number} amount - Amount the client's reserve adjusted
 * @property {string | null} amount_paid - Amount paid out for specific types of adjustments
 * @property {string | null} changelog_notes - Additional notes used to form specific FE messages
 * @property {string} client_id - ID of the associated client
 * @property {string | null} client_paymentid - Associated client for specific types of adjustments
 * @property {string} created_at - When the reserve was created
 * @property {string | null} debtor_payment_id - Associated debtor for specific types of adjustments
 * @property {string | null} deleted_at - When the reserve was deleted
 * @property {string | null} deleted_by - Who deleted the reserve
 * @property {string | null} firebase_key - Key for the firebase entry for legacy invoices
 * @property {string} id - ID of the reserve
 * @property {string | null} invoice_id - Associated invoice
 * @property {object | null} metadata - Additional information associated with specific, more complex adjustments
 * @property {string | null} metadata.check_number - Check number associated with a "non-factored payment"
 * @property {string | null} metadata.date_received - Moment instance of the date received formatted to 'x'
 * @property {string | null} metadata.debtor_id - Debtor associated with a "non-factored payment"
 * @property {string | null} metadata.debtor_name - Debtor associated with a "non-factored payment"
 * @property {string | null} metadata.load_number - Load number representing the invoice for a "non-factored payment"
 * @property {string | null} metadata.third_party - Name of third party (note field)
 * @property {'ach' | 'check' | null} metadata.transfer_type - ACH or check for a "non-factored payment"
 * @property {string} nacha_sign - Whether the posted or updated reserve was a positive or negative adjustment
 * @property {string | null} notes - Notes explaining the reason
 * @property {string | null} quickbooks_journal_entry_id - No clue, good luck
 * @property {string} reason - Reason for the reserve adjustment
 * @property {string} status_client_payment - Has the client been paid
 * @property {number} total - Associated client's reserve
 * @property {string | null} transfer_clientid - Associated client for "transfer reserve" adjustments
 * @property {string} type - Type of reserved
 * @property {string} updated_at - When the reserve was updated
 * @property {string} updated_by - Who updated the reserve
 * @property {object} updated_by_user - User information about who updated the reserve
 * @property {object} updated_by_user.employee - Employee information about who updated the reserve
 * @property {string} updated_by_user.employee.first_name - First name of said employee
 * @returns {V1ReserveResponse}
 */
export const mapV2ReserveResponseToUIExpectations = (v2ReserveResponse) => {
  const uiExpectation = {
    amount: mapV2ResponseAmountToV1ResponseAmount(v2ReserveResponse.amount),
    amount_paid: null,
    changelog_notes: null,
    client_id: v2ReserveResponse.clientId,
    client_paymentid: v2ReserveResponse?.clientPaymentId,
    created_at: v2ReserveResponse.createdAt,
    created_by: v2ReserveResponse.createdBy,
    debtor_payment_id: v2ReserveResponse?.brokerPaymentId,
    deleted_at: v2ReserveResponse?.deletedAt,
    deleted_by: v2ReserveResponse?.deletedBy,
    firebase_key: null,
    id: v2ReserveResponse.id,
    invoice_id: v2ReserveResponse?.invoiceId,
    metadata: {
      check_number: v2ReserveResponse.payload?.checkNumber,
      date_received: v2ReserveResponse.payload?.batchDate || v2ReserveResponse.payload?.receivedDate,
      debtor_id: v2ReserveResponse.payload?.brokerId,
      debtor_name: v2ReserveResponse.payload?.brokerName,
      load_number: v2ReserveResponse.payload?.loadNumber,
      third_party: v2ReserveResponse.payload?.thirdParty,
      transfer_type: v2ReserveResponse.payload?.type,
    },
    nacha_sign: mapV2ReserveResponseAmountToV1NachaSign(v2ReserveResponse.amount),
    notes: v2ReserveResponse.note,
    quickbooks_journal_entry_id: null,
    reason: v2ReserveResponse.reason,
    status_client_payment: null,
    total: mapV2ResponseAmountToV1ResponseAmount(v2ReserveResponse.total),
    transfer_clientid: v2ReserveResponse.payload?.transferClientId,
    type: mapV2ReserveResponseToV1Type(v2ReserveResponse),
    updated_at: v2ReserveResponse.updatedAt,
    updated_by: v2ReserveResponse.updatedBy,
    updated_by_user: v2ReserveResponse.updatedByUser,
  }

  return uiExpectation
}
